// ─────────────────────────────────────────────────────────────
// BulkImport.js — CSV import flow for transactions
// ─────────────────────────────────────────────────────────────
// Steps:
//   1. Upload — pick a CSV file
//   2. Map    — match CSV columns to our fields
//   3. Review — auto-categorize, dedupe, manual touch-ups
//   4. Import — insert in chunks with progress
// ─────────────────────────────────────────────────────────────

import React, { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import Papa from 'papaparse'
import { supabase } from '../lib/supabase'
import { emitDataEvent, EVENTS } from '../lib/dataEvents'
import { theme } from '../lib/theme'
import MatchPrompt from './MatchPrompt'
import {
  normalizeMerchant, findBillMatch, findDebtMatch,
  loadAllMerchantRules, matchRule,
  applyTransactionToBill, applyTransactionToDebt,
} from '../lib/merchantMatching'

const c = theme.colors
const f = theme.fonts

export default function BulkImport({ onClose, partnerId }) {
  const [step, setStep] = useState('upload') // upload | map | review | import | done
  const [file, setFile] = useState(null)
  const [rawRows, setRawRows] = useState([])
  const [headers, setHeaders] = useState([])
  const [mapping, setMapping] = useState(null)
  const [preparedRows, setPreparedRows] = useState([])


  return (
    <div style={styles.overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div style={styles.sheet}>
        <div style={styles.header}>
          <span style={styles.title}>Import Transactions</span>
          <button onClick={onClose} style={styles.closeBtn} aria-label="Close">✕</button>
        </div>
        <div style={styles.body}>
          {step === 'upload' && <UploadStep onFile={(f, rows, hdrs) => { setFile(f); setRawRows(rows); setHeaders(hdrs); setStep('map') }} />}
          {step === 'map' && (
            <MapStep
              headers={headers}
              sampleRow={rawRows[0] || {}}
              onBack={() => setStep('upload')}
              onContinue={(mapping) => { setMapping(mapping); setStep('review') }}
            />
          )}
          {step === 'review' && (
            <ReviewStep
              rawRows={rawRows}
              mapping={mapping}
              partnerId={partnerId}
              onBack={() => setStep('map')}
              onContinue={(prepared) => { setPreparedRows(prepared); setStep('import') }}
            />
          )}
          {step === 'import' && (
            <ImportStep
              rows={preparedRows}
              partnerId={partnerId}
              onClose={onClose}
            />
          )}
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────
// Step 1: Upload
// ─────────────────────────────────────────────────────────────

function UploadStep({ onFile }) {
  const [error, setError] = useState('')
  const [parsing, setParsing] = useState(false)
  const inputRef = useRef(null)

  const handleFile = (file) => {
    if (!file) return
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Only CSV files are supported. If you have an Excel file, save it as CSV first.')
      return
    }
    setParsing(true)
    setError('')
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        setParsing(false)
        if (results.errors.length > 0) {
          setError(`CSV parse error: ${results.errors[0].message}`)
          return
        }
        if (!results.data || results.data.length === 0) {
          setError('No rows found in this file.')
          return
        }
        const headers = results.meta.fields || []
        if (headers.length === 0) {
          setError('No column headers detected. Make sure the first row contains column names.')
          return
        }
        onFile(file, results.data, headers)
      },
      error: (err) => {
        setParsing(false)
        setError(`Could not read file: ${err.message}`)
      },
    })
  }

  return (
    <div style={{ textAlign: 'center', padding: '24px' }}>
      <div style={{ fontSize: '48px', marginBottom: '16px' }}>📄</div>
      <div style={styles.stepTitle}>Drop your CSV file</div>
      <div style={styles.stepHelp}>
        Export from your bank, Mint, Monarch, YNAB, or any spreadsheet — we'll handle the formatting.
      </div>

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault()
          handleFile(e.dataTransfer.files[0])
        }}
        onClick={() => inputRef.current?.click()}
        style={styles.dropzone}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => handleFile(e.target.files[0])}
          style={{ display: 'none' }}
        />
        <div style={{ fontSize: '14px', color: c.inkMuted }}>
          {parsing ? 'Reading file…' : 'Click to choose, or drag and drop'}
        </div>
      </div>

      {error && <div style={styles.error}>{error}</div>}
    </div>
  )
}
// ─────────────────────────────────────────────────────────────
// Step 2: Map columns
// ─────────────────────────────────────────────────────────────

function MapStep({ headers, sampleRow, onBack, onContinue }) {
  // Auto-detect columns based on common header names
  const guess = (candidates) => {
    const lower = headers.map(h => h.toLowerCase())
    for (const cand of candidates) {
      const idx = lower.findIndex(h => h === cand.toLowerCase())
      if (idx >= 0) return headers[idx]
    }
    for (const cand of candidates) {
      const idx = lower.findIndex(h => h.includes(cand.toLowerCase()))
      if (idx >= 0) return headers[idx]
    }
    return ''
  }

  const [dateCol, setDateCol] = useState(guess(['date', 'transaction date', 'posted', 'posting date']))
  const [amountCol, setAmountCol] = useState(guess(['amount', 'debit', 'value']))
  const [merchantCol, setMerchantCol] = useState(guess(['merchant', 'description', 'payee', 'name', 'transaction']))
  const [categoryCol, setCategoryCol] = useState(guess(['category', 'type']))
  const [noteCol, setNoteCol] = useState(guess(['note', 'memo', 'notes']))
  const [paymentCol, setPaymentCol] = useState(guess(['payment', 'payment method', 'card', 'account', 'paid with', 'method']))
  const [signConvention, setSignConvention] = useState('negative-expense') // 'negative-expense' or 'positive-expense'

  const required = dateCol && amountCol && merchantCol

  const Row = ({ label, value, onChange, optional }) => (
    <div style={{ marginBottom: '14px' }}>
      <label style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', fontWeight: 600, color: c.plum, marginBottom: '6px' }}>
        <span>{label}</span>
        {optional && <span style={{ color: c.inkMuted, fontWeight: 400 }}>Optional</span>}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%', padding: '10px 12px',
          background: c.cream2, border: `1px solid ${c.line}`,
          borderRadius: '8px', fontFamily: f.body, fontSize: '14px',
          color: c.plum, outline: 'none', boxSizing: 'border-box',
        }}
      >
        <option value="">— Select column —</option>
        {headers.map(h => (
          <option key={h} value={h}>{h}</option>
        ))}
      </select>
      {value && sampleRow[value] && (
        <div style={{ fontSize: '11px', color: c.inkMuted, marginTop: '4px' }}>
          Sample: <span style={{ color: c.plum }}>{String(sampleRow[value]).slice(0, 60)}</span>
        </div>
      )}
    </div>
  )

  return (
    <div>
      <div style={styles.stepTitle}>Map your columns</div>
      <div style={styles.stepHelp}>
        Tell us which CSV column means what. We've guessed based on the headers.
      </div>

      <Row label="Date" value={dateCol} onChange={setDateCol} />
      <Row label="Amount" value={amountCol} onChange={setAmountCol} />
      <Row label="Merchant / Description" value={merchantCol} onChange={setMerchantCol} />
      <Row label="Category" value={categoryCol} onChange={setCategoryCol} optional />
      <Row label="Payment method" value={paymentCol} onChange={setPaymentCol} optional />
      <Row label="Note / Memo" value={noteCol} onChange={setNoteCol} optional />

      <div style={{ marginTop: '20px', padding: '14px', background: c.cream2, borderRadius: '10px', border: `1px solid ${c.line}` }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: c.plum, marginBottom: '8px' }}>Amount sign convention</div>
        <div style={{ fontSize: '12px', color: c.inkMuted, marginBottom: '10px', lineHeight: 1.5 }}>
          How does your bank format amounts?
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            onClick={() => setSignConvention('negative-expense')}
            style={{
              flex: 1, padding: '10px', borderRadius: '8px',
              border: `1.5px solid ${signConvention === 'negative-expense' ? c.plum : c.line}`,
              background: signConvention === 'negative-expense' ? c.plum : 'transparent',
              color: signConvention === 'negative-expense' ? c.cream : c.plum,
              fontFamily: f.body, fontSize: '13px', fontWeight: 600, cursor: 'pointer',
            }}
          >
            Negative = expense
          </button>
          <button
            type="button"
            onClick={() => setSignConvention('positive-expense')}
            style={{
              flex: 1, padding: '10px', borderRadius: '8px',
              border: `1.5px solid ${signConvention === 'positive-expense' ? c.plum : c.line}`,
              background: signConvention === 'positive-expense' ? c.plum : 'transparent',
              color: signConvention === 'positive-expense' ? c.cream : c.plum,
              fontFamily: f.body, fontSize: '13px', fontWeight: 600, cursor: 'pointer',
            }}
          >
            Positive = expense
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '10px', marginTop: '24px' }}>
        <button type="button" onClick={onBack} style={styles.btnSecondary}>Back</button>
        <button
          type="button"
          onClick={() => onContinue({ dateCol, amountCol, merchantCol, categoryCol, paymentCol, noteCol, signConvention })}
          disabled={!required}
          style={{ ...styles.btnPrimary, opacity: required ? 1 : 0.5, cursor: required ? 'pointer' : 'not-allowed' }}
        >
          Continue →
        </button>
      </div>
    </div>
  )
}
// ─────────────────────────────────────────────────────────────
// Step 3: Review — parse, categorize, dedupe
// ─────────────────────────────────────────────────────────────

// Parse a date string in many common formats. Returns YYYY-MM-DD or null.
function parseDate(str) {
  if (!str) return null
  const s = String(str).trim()
  if (!s) return null

  // YYYY-MM-DD
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/)
  if (m) {
    return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`
  }

  // MM/DD/YYYY or M/D/YY
  m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/)
  if (m) {
    let year = m[3]
    if (year.length === 2) year = `20${year}`
    return `${year}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}`
  }

  // MM-DD-YYYY
  m = s.match(/^(\d{1,2})-(\d{1,2})-(\d{4})/)
  if (m) {
    return `${m[3]}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}`
  }

  // Fallback to JS Date.parse
  const d = new Date(s)
  if (!isNaN(d.getTime())) {
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }
  return null
}

function parseAmount(str, signConvention) {
  if (!str) return null
  // Strip currency symbols, spaces, commas
  const cleaned = String(str).replace(/[$,\s]/g, '')
  const n = parseFloat(cleaned)
  if (isNaN(n)) return null
  // Always store as positive (we treat all imports as expenses)
  if (signConvention === 'negative-expense') {
    // Negative values = expenses → make positive
    return n < 0 ? Math.abs(n) : n
  } else {
    // Positive values = expenses → already positive
    return Math.abs(n)
  }
}

function ReviewStep({ rawRows, mapping, partnerId, onBack, onContinue }) {
  const [loading, setLoading] = useState(true)
  const [prepared, setPrepared] = useState([])
  const [allCategories, setAllCategories] = useState([])
  const [budgetsByMonth, setBudgetsByMonth] = useState({})
  const [stats, setStats] = useState({ ready: 0, needsCategory: 0, duplicates: 0, invalid: 0 })

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => { prepareRows() }, [])

  const prepareRows = async () => {
    setLoading(true)
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) { setLoading(false); return }
    const userIds = [user.id, ...(partnerId ? [partnerId] : [])]

    // Fetch existing transactions for dedup + merchant memory
    const [existingTxRes, catsRes, budgetsRes, pmRes] = await Promise.all([
      supabase.from('transactions')
        .select('transaction_date, amount, merchant_name, category_id')
        .in('user_id', userIds)
        .limit(2000),
      supabase.from('budget_categories')
        .select('id, name, parent_id, budget_id, budgets!inner(month, year)')
        .in('user_id', userIds),
      supabase.from('budgets')
        .select('id, month, year')
        .in('user_id', userIds)
        .eq('is_active', true),
      supabase.from('payment_methods')
        .select('id, name, last_four')
        .in('user_id', userIds),
    ])

    const existing = existingTxRes.data || []
    const cats = catsRes.data || []
    const budgets = budgetsRes.data || []

    // Build budget lookup: { '2026-3': budget_id }
    const budgetLookup = {}
    budgets.forEach(b => { budgetLookup[`${b.year}-${b.month}`] = b.id })

    // Build merchant memory: most-recent category per merchant
    const merchantMemory = {} // { 'kroger': cat_id }
    existing.forEach(tx => {
      const key = (tx.merchant_name || '').toLowerCase().trim()
      if (key && tx.category_id && !merchantMemory[key]) {
        merchantMemory[key] = tx.category_id
      }
    })

    // Build dedup index: 'YYYY-MM-DD-amount-merchant' → existing tx
    const dedupKey = (date, amount, merchant) =>
      `${date}-${parseFloat(amount).toFixed(2)}-${(merchant || '').toLowerCase().trim()}`
    const existingKeys = new Set(
      existing.map(tx => dedupKey(tx.transaction_date, tx.amount, tx.merchant_name))
    )

    // Process each row
    const out = []
    rawRows.forEach((row, idx) => {
      const dateStr = row[mapping.dateCol]
      const amountStr = row[mapping.amountCol]
      const merchant = String(row[mapping.merchantCol] || '').trim()
      const noteStr = mapping.noteCol ? String(row[mapping.noteCol] || '').trim() : ''
      const csvCategory = mapping.categoryCol ? String(row[mapping.categoryCol] || '').trim() : ''

      const paymentStr = mapping.paymentCol ? String(row[mapping.paymentCol] || '').trim() : ''

      const date = parseDate(dateStr)
      const amount = parseAmount(amountStr, mapping.signConvention)

      if (!date || !amount || !merchant) {
        out.push({
          idx,
          status: 'invalid',
          rawDate: dateStr,
          rawAmount: amountStr,
          merchant,
          reason: !date ? 'Could not parse date' : !amount ? 'Could not parse amount' : 'Missing merchant',
        })
        return
      }

      // Dedup check
      if (existingKeys.has(dedupKey(date, amount, merchant))) {
        out.push({
          idx, status: 'duplicate',
          date, amount, merchant, note: noteStr,
        })
        return
      }

      // Find category by merchant memory or CSV category
      let categoryId = merchantMemory[merchant.toLowerCase()] || null
      if (!categoryId && csvCategory) {
        // Try to match CSV category name to one of our categories
        const match = cats.find(c =>
          c.name.toLowerCase() === csvCategory.toLowerCase() &&
          c.parent_id !== null // prefer subcategories
        )
        if (match) categoryId = match.id
      }

      // Re-route to the equivalent category in this row's own budget.
      // merchantMemory points at the FIRST budget that ever categorized this merchant
      // (e.g. January). The row probably belongs to a different month — find the
      // equivalent there. Same logic as updateRowCategory.
      if (categoryId) {
        const txDateForLookup = new Date(date)
        const txYearForLookup = txDateForLookup.getFullYear()
        const txMonthForLookup = txDateForLookup.getMonth() + 1
        const targetBudgetId = budgetLookup[`${txYearForLookup}-${txMonthForLookup}`]
        if (targetBudgetId) {
          const picked = cats.find(c => c.id === categoryId)
          if (picked && picked.budget_id !== targetBudgetId) {
            const pickedParentName = picked.parent_id
              ? (cats.find(p => p.id === picked.parent_id) || {}).name || null
              : null
            const equivalent = cats.find(c => {
              if (c.budget_id !== targetBudgetId) return false
              if ((c.name || '').toLowerCase().trim() !== (picked.name || '').toLowerCase().trim()) return false
              if (!pickedParentName) return c.parent_id === null
              const candidateParentName = (cats.find(p => p.id === c.parent_id) || {}).name || null
              return candidateParentName && candidateParentName.toLowerCase().trim() === pickedParentName.toLowerCase().trim()
            })
            if (equivalent) categoryId = equivalent.id
          }
        }
      }

      // Find payment method by name or last 4
      let paymentMethodId = null
      if (paymentStr) {
        const lowerPay = paymentStr.toLowerCase()
        const pms = pmRes.data || []
        // Try exact name match, then last_four match, then substring
        let pm = pms.find(p => p.name && p.name.toLowerCase() === lowerPay)
        if (!pm) pm = pms.find(p => p.last_four && lowerPay.includes(p.last_four))
        if (!pm) pm = pms.find(p => p.name && lowerPay.includes(p.name.toLowerCase()))
        if (pm) paymentMethodId = pm.id
      }

      // Find budget for this transaction's month.
      // Parse y/m directly from the ISO string to avoid Date() timezone shifts
      // (in CST/CDT, `new Date('2026-03-01')` becomes Feb 28 6pm local).
      const [txYearStr, txMonthStr] = date.split('-')
      const txYear = parseInt(txYearStr, 10)
      const txMonth = parseInt(txMonthStr, 10)
      const budgetId = budgetLookup[`${txYear}-${txMonth}`] || null

      out.push({
        idx,
        status: categoryId ? 'ready' : 'needs-category',
        date, amount, merchant, note: noteStr,
        category_id: categoryId,
        payment_method_id: paymentMethodId,
        budget_id: budgetId,
        needsBudget: !budgetId,
      })
    })

    setPrepared(out)
    setAllCategories(cats)
    setBudgetsByMonth(budgetLookup)

    setStats({
      ready: out.filter(r => r.status === 'ready').length,
      needsCategory: out.filter(r => r.status === 'needs-category').length,
      duplicates: out.filter(r => r.status === 'duplicate').length,
      invalid: out.filter(r => r.status === 'invalid').length,
    })
    setLoading(false)
  }

  const updateRowCategory = (idx, categoryId) => {
    setPrepared(prev => prev.map(r => {
      if (r.idx !== idx) return r
      let resolvedId = categoryId
      // Re-route the picked category to the equivalent in the row's own budget.
      // The dropdown is dedup'd, so the picked UUID may belong to another month's
      // budget. Find a category with the same name+parent inside r.budget_id.
      if (categoryId && r.budget_id) {
        const picked = allCategories.find(c => c.id === categoryId)
        if (picked) {
          const pickedParentName = (allCategories.find(c => c.id === picked.parent_id) || {}).name || null
          const equivalent = allCategories.find(c => {
            if (c.budget_id !== r.budget_id) return false
            if (c.name.toLowerCase().trim() !== picked.name.toLowerCase().trim()) return false
            if (!pickedParentName) return c.parent_id === null
            const candidateParentName = (allCategories.find(p => p.id === c.parent_id) || {}).name || null
            return candidateParentName && candidateParentName.toLowerCase().trim() === pickedParentName.toLowerCase().trim()
          })
          if (equivalent) resolvedId = equivalent.id
        }
      }
      return {
        ...r,
        category_id: resolvedId,
        status: resolvedId ? 'ready' : 'needs-category',
      }
    }))
    // Recompute stats from actual row state — arithmetic drift was producing
    // negative counts when categories got changed multiple times.
    setPrepared(curr => {
      const ready         = curr.filter(r => r.status === 'ready').length
      const needsCategory = curr.filter(r => r.status === 'needs-category').length
      const duplicates    = curr.filter(r => r.status === 'duplicate').length
      const invalid       = curr.filter(r => r.status === 'invalid').length
      setStats({ ready, needsCategory, duplicates, invalid })
      return curr
    })
  }

  const importable = prepared.filter(r => r.status === 'ready')
  const canContinue = importable.length > 0

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: c.inkMuted }}>Analyzing your CSV…</div>
  }

  return (
    <div>
      <div style={styles.stepTitle}>Review and categorize</div>
      <div style={styles.stepHelp}>
        We've matched what we can. Categorize the rest below.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '20px' }}>
        <Stat label="Ready" value={stats.ready} color="#22C55E" />
        <Stat label="Needs Category" value={stats.needsCategory} color="#F59E0B" />
        <Stat label="Duplicates" value={stats.duplicates} color="#8899AA" />
        <Stat label="Invalid" value={stats.invalid} color="#EF4444" />
      </div>

      <div style={{ maxHeight: '320px', overflowY: 'auto', border: `1px solid ${c.line}`, borderRadius: '10px' }}>
        {prepared.map(row => (
          <div key={row.idx} style={{
            padding: '10px 12px',
            borderBottom: `1px solid ${c.line}`,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontSize: '13px',
            background: row.status === 'invalid' ? '#FEF2F2' : row.status === 'duplicate' ? c.cream2 : 'transparent',
            opacity: (row.status === 'invalid' || row.status === 'duplicate') ? 0.65 : 1,
          }}>
            <div style={{ width: '90px', flexShrink: 0, color: c.inkMuted }}>{row.date || row.rawDate}</div>
            <div style={{ width: '70px', flexShrink: 0, fontWeight: 600, color: c.plum, textAlign: 'right' }}>
              {row.amount ? `$${row.amount.toFixed(2)}` : row.rawAmount || '—'}
            </div>
            <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {row.merchant || '—'}
            </div>
            {row.status === 'invalid' && (
              <div style={{ fontSize: '11px', color: '#A32D2D' }}>{row.reason}</div>
            )}
            {row.status === 'duplicate' && (
              <div style={{ fontSize: '11px', color: c.inkMuted, fontStyle: 'italic' }}>Already exists</div>
            )}
            {(row.status === 'ready' || row.status === 'needs-category') && (
              <select
                value={(() => {
                  if (!row.category_id) return ''
                  const rowCat = allCategories.find(c => c.id === row.category_id)
                  if (!rowCat) return ''
                  const parentNameById = {}
                  allCategories.forEach(c => {
                    if (c.parent_id === null) parentNameById[c.id] = (c.name || '').toLowerCase().trim()
                  })
                  const rowParentName = rowCat.parent_id ? parentNameById[rowCat.parent_id] : ''
                  const rowName = (rowCat.name || '').toLowerCase().trim()
                  const seen = new Set()
                  const sorted = allCategories.filter(c => c.parent_id !== null).slice().sort((a, b) => a.name.localeCompare(b.name))
                  for (const c of sorted) {
                    const pn = parentNameById[c.parent_id] || ''
                    const key = `${pn}::${(c.name || '').toLowerCase().trim()}`
                    if (seen.has(key)) continue
                    seen.add(key)
                    if (pn === rowParentName && (c.name || '').toLowerCase().trim() === rowName) {
                      return c.id
                    }
                  }
                  return ''
                })()}
                onChange={(e) => updateRowCategory(row.idx, e.target.value || null)}
                style={{
                  padding: '6px 8px', borderRadius: '6px',
                  border: `1px solid ${row.status === 'needs-category' ? '#F59E0B' : c.line}`,
                  background: row.status === 'needs-category' ? '#FEF3C7' : c.cream2,
                  fontSize: '12px', color: c.plum,
                  width: '180px', maxWidth: '180px',
                }}
              >
                <option value="">— Pick category —</option>
                {(() => {
                  // Dedupe by parent NAME + own NAME — parent_id is a UUID that differs
                  // across monthly budgets even when the parent has the same name. So we
                  // must compare on names to collapse properly. Sort once at the end.
                  const seen = new Set()
                  const parentNameById = {}
                  allCategories.forEach(c => {
                    if (c.parent_id === null) parentNameById[c.id] = (c.name || '').toLowerCase().trim()
                  })
                  return allCategories
                    .filter(cat => cat.parent_id !== null)
                    .filter(cat => {
                      const parentName = parentNameById[cat.parent_id] || ''
                      const key = `${parentName}::${(cat.name || '').toLowerCase().trim()}`
                      if (seen.has(key)) return false
                      seen.add(key)
                      return true
                    })
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))
                })()}
              </select>
            )}
          </div>
        ))}
      </div>

      <div style={{ marginTop: '14px', fontSize: '12px', color: c.inkMuted, lineHeight: 1.5 }}>
        Importing <strong style={{ color: c.plum }}>{importable.length}</strong> transactions.
        {stats.needsCategory > 0 && ` ${stats.needsCategory} skipped (need category).`}
        {stats.duplicates > 0 && ` ${stats.duplicates} skipped (duplicates).`}
        {stats.invalid > 0 && ` ${stats.invalid} skipped (invalid).`}
      </div>

      <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
        <button type="button" onClick={onBack} style={styles.btnSecondary}>Back</button>
        <button
          type="button"
          onClick={() => onContinue(importable)}
          disabled={!canContinue}
          style={{ ...styles.btnPrimary, opacity: canContinue ? 1 : 0.5, cursor: canContinue ? 'pointer' : 'not-allowed' }}
        >
          Import {importable.length} →
        </button>
      </div>
    </div>
  )
}

function Stat({ label, value, color }) {
  return (
    <div style={{
      padding: '10px',
      background: c.cream2,
      borderRadius: '8px',
      textAlign: 'center',
      border: `1px solid ${c.line}`,
    }}>
      <div style={{ fontSize: '20px', fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: '11px', color: c.inkMuted, marginTop: '2px' }}>{label}</div>
    </div>
  )
}
// ─────────────────────────────────────────────────────────────
// Step 4: Import — bulk insert with progress
// ─────────────────────────────────────────────────────────────

function ImportStep({ rows, partnerId, onClose }) {
  const [phase, setPhase] = useState('confirm') // confirm | importing | done
  const [progress, setProgress] = useState(0)
  const [imported, setImported] = useState(0)
  const [errors, setErrors] = useState([])
  const [createdBudgets, setCreatedBudgets] = useState(0)
  // Chunk 5 — post-import bill/debt match review.
  const [reviewQueue, setReviewQueue] = useState([]) // [{ key, kind, target, merchantName, transactions, userId }]
  const [reviewIndex, setReviewIndex] = useState(-1) // -1 = not reviewing; >=0 = current group
  const [reviewDone, setReviewDone] = useState(false)
  const [matchLists, setMatchLists] = useState({ bills: [], debts: [] }) // for MatchPrompt's picker

  const monthsNeeded = React.useMemo(() => {
    const months = new Set()
    rows.forEach(r => {
      if (r.needsBudget) {
        const [y, m] = (r.date || '').split('-')
        months.add(`${parseInt(y, 10)}-${parseInt(m, 10)}`)
      }
    })
    return Array.from(months)
  }, [rows])

  const runImport = async () => {
    setPhase('importing')
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) { setPhase('confirm'); return }

    // Step A: Create skeleton budgets for any missing months
    let createdCount = 0
    const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December']
    const newBudgetIds = {} // 'YYYY-M' → budget_id

    for (const ym of monthsNeeded) {
      const [y, m] = ym.split('-').map(Number)
      const { data: created } = await supabase.from('budgets').insert({
        user_id: user.id,
        month: m,
        year: y,
        is_active: true,
        total_amount: 0,
        name: `${monthNames[m - 1]} ${y}`,
      }).select().single()
      if (created) {
        newBudgetIds[ym] = created.id
        createdCount++

        // Copy categories from the most recent prior budget so transactions
        // can land in named categories instead of orphaning. Mirrors
        // createBudgetForViewMonth in Plan.js.
        const { data: priorBudgets } = await supabase.from('budgets')
          .select('id, year, month')
          .eq('user_id', user.id)
          .eq('is_active', true)
          .neq('id', created.id)
          .or(`year.lt.${y},and(year.eq.${y},month.lt.${m})`)
          .order('year', { ascending: false })
          .order('month', { ascending: false })
          .limit(1)

        if (priorBudgets && priorBudgets.length > 0) {
          const priorBudgetId = priorBudgets[0].id
          const { data: priorCats } = await supabase.from('budget_categories')
            .select('*')
            .eq('budget_id', priorBudgetId)
            .order('sort_order')

          if (priorCats && priorCats.length > 0) {
            const parents = priorCats.filter(c => !c.parent_id)
            const subs = priorCats.filter(c => c.parent_id)
            const oldToNewParentId = {}

            for (const p of parents) {
              const { data: newP } = await supabase.from('budget_categories').insert({
                user_id: user.id,
                budget_id: created.id,
                name: p.name,
                icon: p.icon,
                limit_amount: parseFloat(p.limit_amount || 0),
                sort_order: p.sort_order,
                parent_id: null,
              }).select().single()
              if (newP) oldToNewParentId[p.id] = newP.id
            }

            if (subs.length > 0) {
              const subInserts = subs.map(s => ({
                user_id: user.id,
                budget_id: created.id,
                name: s.name,
                icon: s.icon,
                limit_amount: parseFloat(s.limit_amount || 0),
                sort_order: s.sort_order,
                parent_id: oldToNewParentId[s.parent_id] || null,
              }))
              await supabase.from('budget_categories').insert(subInserts)
            }
          }
        }
      }
    }
    if (createdCount > 0) {
      emitDataEvent(EVENTS.BUDGET_SAVED)
      emitDataEvent(EVENTS.CATEGORY_SAVED)
    }
    setCreatedBudgets(createdCount)

    // Step B: Re-fetch budgets so each row has a valid budget_id
    const allBudgetsRes = await supabase.from('budgets')
      .select('id, month, year')
      .eq('user_id', user.id)
      .eq('is_active', true)
    const budgetLookup = {}
    ;(allBudgetsRes.data || []).forEach(b => { budgetLookup[`${b.year}-${b.month}`] = b.id })

    // Step C: Insert in chunks of 50
    const CHUNK_SIZE = 50
    let totalImported = 0
    const errorList = []
    const insertedTxs = [] // Chunk 5: keep inserted rows for the match pass

    for (let i = 0; i < rows.length; i += CHUNK_SIZE) {
      const chunk = rows.slice(i, i + CHUNK_SIZE)
      const inserts = chunk.map(r => {
        // Parse y/m directly from the ISO string — see prepareRows comment
        const [yStr, mStr] = (r.date || '').split('-')
        const ym = `${parseInt(yStr, 10)}-${parseInt(mStr, 10)}`
        return {
          user_id: user.id,
          transaction_date: r.date,
          amount: r.amount,
          merchant_name: r.merchant,
          category_id: r.category_id,
          payment_method_id: r.payment_method_id || null,
          budget_id: budgetLookup[ym] || null,
          note: r.note || null,
          type: 'expense',
        }
      })
      const { data: insertedChunk, error } = await supabase.from('transactions')
        .insert(inserts)
        .select('id, merchant_name, amount, transaction_date')
      if (error) {
        errorList.push(`Chunk ${Math.floor(i / CHUNK_SIZE) + 1}: ${error.message}`)
      } else {
        totalImported += chunk.length
        insertedTxs.push(...(insertedChunk || []))
      }
      setProgress(Math.min(100, Math.round(((i + chunk.length) / rows.length) * 100)))
      setImported(totalImported)
    }

    setErrors(errorList)
    if (totalImported > 0) {
      emitDataEvent(EVENTS.TRANSACTION_SAVED)
    }

    // Step D (Chunk 5): auto-match imported rows against bills/debts.
    // Saved rules apply silently; unmatched potential matches queue for review.
    await runBulkMatchPass(user, insertedTxs)

    setPhase('done')
  }

  // Chunk 5 — bulk match pass. Applies saved rules silently; groups the
  // remaining potential matches by (merchant + target) so the review asks once
  // per group and applies the answer to every row in it (no repeat-prompt storm).
  const runBulkMatchPass = async (user, insertedTxs) => {
    if (!insertedTxs || insertedTxs.length === 0) return
    const userIds = [user.id, ...(partnerId ? [partnerId] : [])]
    const [{ data: billsData }, { data: debtsData }, rules] = await Promise.all([
      supabase.from('bills').select('id, name, amount, category_id').in('user_id', userIds).eq('is_active', true),
      supabase.from('debts').select('id, name, current_balance, monthly_payment').in('user_id', userIds),
      loadAllMerchantRules(user.id),
    ])
    const bills = billsData || []
    const debts = debtsData || []
    setMatchLists({ bills, debts })

    const groups = {} // key → { key, kind, target, merchantName, transactions, userId }
    for (const tx of insertedTxs) {
      const norm = normalizeMerchant(tx.merchant_name)
      if (!norm) continue
      const paidAt = tx.transaction_date ? new Date(tx.transaction_date + 'T12:00:00').toISOString() : null

      const billHit = findBillMatch(tx, bills)
      if (billHit) {
        const bill = billHit.bill
        if (matchRule(rules, norm, 'apply_to_bill', bill.id)) { await applyTransactionToBill(tx.id, bill.id); continue }
        if (matchRule(rules, norm, 'never_match', bill.id)) continue
        const key = `bill:${bill.id}:${norm}`
        ;(groups[key] = groups[key] || { key, kind: 'bill', target: bill, merchantName: tx.merchant_name, transactions: [], userId: user.id })
          .transactions.push({ id: tx.id, amount: tx.amount, transaction_date: tx.transaction_date })
        continue
      }

      const debtHit = findDebtMatch(tx, debts)
      if (debtHit) {
        const debt = debtHit.debt
        if (matchRule(rules, norm, 'apply_to_debt', debt.id)) { await applyTransactionToDebt(tx.id, debt.id, tx.amount, paidAt); continue }
        if (matchRule(rules, norm, 'never_match', debt.id)) continue
        const key = `debt:${debt.id}:${norm}`
        ;(groups[key] = groups[key] || { key, kind: 'debt', target: debt, merchantName: tx.merchant_name, transactions: [], userId: user.id })
          .transactions.push({ id: tx.id, amount: tx.amount, transaction_date: tx.transaction_date })
        continue
      }
    }
    setReviewQueue(Object.values(groups))
  }

  const advanceReview = () => {
    setReviewIndex(i => {
      const next = i + 1
      if (next >= reviewQueue.length) { setReviewDone(true); return -1 }
      return next
    })
  }

  if (phase === 'confirm') {
    return (
      <div>
        <div style={styles.stepTitle}>Ready to import</div>
        <div style={styles.stepHelp}>
          About to import <strong style={{ color: c.plum }}>{rows.length}</strong> transactions.
        </div>

        {monthsNeeded.length > 0 && (
          <div style={{ padding: '14px', background: c.cream2, border: `1px solid ${c.line}`, borderRadius: '10px', marginBottom: '20px', fontSize: '13px', color: c.plum, lineHeight: 1.55 }}>
            Some transactions belong to months you don't have plans for yet.
            We'll create empty plans for: <strong>{monthsNeeded.length} {monthsNeeded.length === 1 ? 'month' : 'months'}</strong>.
            You can fill in the limits later.
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
          <button type="button" onClick={onClose} style={styles.btnSecondary}>Cancel</button>
          <button type="button" onClick={runImport} style={styles.btnPrimary}>
            Import {rows.length} →
          </button>
        </div>
      </div>
    )
  }

  if (phase === 'importing') {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div style={styles.stepTitle}>Importing…</div>
        <div style={{ marginTop: '20px', height: '8px', background: c.cream2, borderRadius: '4px', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${progress}%`, background: c.plum, transition: 'width 0.3s' }} />
        </div>
        <div style={{ marginTop: '10px', fontSize: '13px', color: c.inkMuted }}>
          {imported} of {rows.length} transactions
        </div>
      </div>
    )
  }

  // Chunk 5 — reviewing a queued match group (one prompt per merchant+target).
  if (reviewIndex >= 0 && reviewQueue[reviewIndex]) {
    const group = reviewQueue[reviewIndex]
    return (
      <div style={{ padding: '8px 4px' }}>
        <MatchPrompt
          merchantName={group.merchantName}
          transactions={group.transactions}
          kind={group.kind}
          target={group.target}
          bills={matchLists.bills}
          debts={matchLists.debts}
          userId={group.userId}
          onDone={advanceReview}
          onCancel={advanceReview}
        />
        <div style={{ textAlign: 'center', fontSize: '12px', color: c.inkMuted, marginTop: '4px' }}>
          {reviewIndex + 1} of {reviewQueue.length}
        </div>
      </div>
    )
  }

  const queuedCount = reviewQueue.reduce((s, g) => s + g.transactions.length, 0)

  return (
    <div style={{ padding: '20px', textAlign: 'center' }}>
      <div style={{ fontSize: '48px', marginBottom: '16px' }}>{errors.length === 0 ? '✓' : '⚠️'}</div>
      <div style={styles.stepTitle}>
        {errors.length === 0 ? 'Import complete!' : 'Imported with errors'}
      </div>
      <div style={styles.stepHelp}>
        Imported <strong style={{ color: c.plum }}>{imported}</strong> transactions.
        {createdBudgets > 0 && ` Created ${createdBudgets} new monthly plan${createdBudgets === 1 ? '' : 's'}.`}
      </div>
      {errors.length > 0 && (
        <div style={styles.error}>
          {errors.map((err, i) => <div key={i}>{err}</div>)}
        </div>
      )}
      {reviewQueue.length > 0 && !reviewDone && (
        <div style={styles.reviewCard}>
          <div style={styles.reviewText}>
            {queuedCount} transaction{queuedCount === 1 ? '' : 's'} may match a bill or debt. Review?
          </div>
          <div style={styles.reviewActions}>
            <button type="button" onClick={() => setReviewDone(true)} style={styles.btnSecondary}>
              Not now
            </button>
            <button type="button" onClick={() => setReviewIndex(0)} style={styles.btnPrimary}>
              Review matches
            </button>
          </div>
        </div>
      )}
      <button type="button" onClick={onClose} style={{ ...styles.btnPrimary, marginTop: '20px' }}>
        Done
      </button>
    </div>
  )
}
// ─────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────

const styles = {
  overlay: {
    position: 'fixed', inset: 0, overscrollBehaviorX: 'contain',
    background: 'rgba(30, 16, 64, 0.6)',
    backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '20px', fontFamily: f.body,
  },
  sheet: {
    overscrollBehaviorX: 'contain',
    background: c.cream, borderRadius: '20px',
    width: '100%', maxWidth: '640px', maxHeight: '85vh',
    display: 'flex', flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(30, 16, 64, 0.3)',
  },
  header: {
    padding: '18px 24px', display: 'flex',
    alignItems: 'center', justifyContent: 'space-between',
    borderBottom: `1px solid ${c.line}`,
    flexShrink: 0,
  },
  title: { fontFamily: f.display, fontSize: '20px', fontWeight: 600, color: c.plum },
  closeBtn: {
    background: c.cream2, border: 'none',
    width: '32px', height: '32px', borderRadius: '50%',
    fontSize: '14px', color: c.inkMuted, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  body: { padding: '20px 24px', overflow: 'auto', flex: 1 },
  stepTitle: { fontFamily: f.display, fontSize: '22px', fontWeight: 600, color: c.plum, marginBottom: '8px' },
  stepHelp: { fontSize: '14px', color: c.inkMuted, lineHeight: 1.55, marginBottom: '20px' },
  dropzone: {
    border: `2px dashed ${c.lineStrong}`, borderRadius: '12px',
    padding: '40px 20px', cursor: 'pointer',
    background: c.cream2, transition: 'border-color 0.15s',
  },
  error: {
    background: '#FDECEA', color: '#A32D2D',
    padding: '10px 12px', borderRadius: '8px',
    fontSize: '13px', marginTop: '14px', textAlign: 'left',
  },
  btnPrimary: {
    flex: 1, padding: '12px',
    background: c.plum, color: c.cream,
    border: 'none', borderRadius: '10px',
    fontFamily: f.body, fontSize: '14px', fontWeight: 600,
    cursor: 'pointer',
  },
  btnSecondary: {
    flex: 1, padding: '12px',
    background: 'transparent', color: c.plum,
    border: `1px solid ${c.lineStrong}`, borderRadius: '10px',
    fontFamily: f.body, fontSize: '14px', fontWeight: 600,
    cursor: 'pointer',
  },
  // Chunk 5 — post-import "review matches" card
  reviewCard: {
    marginTop: '20px', padding: '16px',
    background: c.greenSoft, borderRadius: '12px',
    border: `1px solid rgba(22, 163, 74, 0.18)`,
    textAlign: 'left',
  },
  reviewText: { fontSize: '14px', color: c.plum, fontWeight: 500, marginBottom: '12px' },
  reviewActions: { display: 'flex', gap: '10px' },
}